import EncryptedCookieGateway from "lib/cookie-gateway";
/**
 * Processes IDs into a consistent integer array format
 */
export const processIdsToIntegerArray = (ids, filterInvalid = true) => {
  if (!ids) {
    return [];
  }

  // If already an array, map to integers
  if (Array.isArray(ids)) {
    const parsed = ids.map((id) => parseInt(id, 10));
    return filterInvalid ? parsed.filter((id) => !isNaN(id)) : parsed;
  }

  // If single value, convert to integer
  const parsed = parseInt(ids, 10);
  if (filterInvalid && isNaN(parsed)) {
    return [];
  }
  return [parsed];
};

export const filterOutNullDepartment = (departmentIds) => {
  return departmentIds.filter((id) => id !== -1);
};

/**
 * Processes null department ID into integer array
 */
export const processNullDepartmentId = (nullId) => {
  if (!nullId) {
    return [];
  }

  const nullIdArray = Array.isArray(nullId) ? nullId : [nullId];
  return nullIdArray.map((id) => parseInt(id, 10));
};

/**
 * Combines regular department IDs with null department IDs
 */
export const combineDepartmentIds = (departmentIds, nullDepartmentIds) => {
  return [...departmentIds, ...nullDepartmentIds];
};

/**
 * Extracts department IDs from URL search params

 */
export const getDepartmentIdsFromUrl = (
  searchParams,
  paramName = "department_ids",
) => {
  const ids = searchParams.getAll(paramName);
  return processIdsToIntegerArray(ids.length ? ids : null);
};

/**
 * Extracts team IDs from URL search params
 */
export const getTeamIdsFromUrl = (searchParams, paramName = "team_ids") => {
  const ids = searchParams.getAll(paramName);
  return processIdsToIntegerArray(ids.length ? ids : null);
};

/**
 * Comprehensive department ID processor
 * Handles cookies, URL params, query params, and state in priority order

 */
export const processDepartmentIds = ({
  cookieValue,
  urlParams,
  queryValue,
  stateValue,
  includeNullDept = false,
  nullDeptValue = null,
}) => {
  // Priority: state > cookie > URL > query
  let departmentIds =
    stateValue && stateValue.length
      ? stateValue
      : cookieValue
        ? cookieValue
        : urlParams && urlParams.length
          ? urlParams
          : queryValue;

  const regularDeptIds = processIdsToIntegerArray(departmentIds);

  if (includeNullDept && nullDeptValue) {
    const nullDeptIds = processNullDepartmentId(nullDeptValue);
    return combineDepartmentIds(regularDeptIds, nullDeptIds);
  }

  return regularDeptIds;
};

/**
 * Comprehensive team ID processor
 * Handles cookies, URL params, query params, and state in priority order
 */
export const processTeamIds = ({
  cookieValue,
  urlParams,
  queryValue,
  stateValue,
}) => {
  // Priority: state > cookie > URL > query
  let teamIds =
    stateValue && stateValue.length
      ? stateValue
      : cookieValue
        ? cookieValue
        : urlParams && urlParams.length
          ? urlParams
          : queryValue;

  return processIdsToIntegerArray(teamIds);
};

export const buildFinalQuery = ({
  query = {},
  preservedDepartmentIds,
  preservedTeamIds,
  preservedNullDept,
} = {}) => {
  const { page, page_size, ...rest } = query;

  return {
    ...rest,
    department_ids: preservedDepartmentIds,
    team_ids: preservedTeamIds,
    null_department: preservedNullDept,
  };
};

// utils/timecardFilters.js
export const getTimecardCommonFilters = ({ query, search }) => {
  const cookieDepartmentIds = EncryptedCookieGateway.get("departmentId") ?? [];
  const cookieTeamIds = EncryptedCookieGateway.get("teamId") ?? [];
  const nullDept = EncryptedCookieGateway.get("nullDept") ?? [];
  const payrollId =
    EncryptedCookieGateway.get("payrollId") ?? query?.payroll_id;

  const startDate =
    EncryptedCookieGateway.get("startDate") ?? query?.start_date;

  const endDate = EncryptedCookieGateway.get("endDate") ?? query?.end_date;

  const searchParams = new URLSearchParams(search);

  const departmentIdsIntegers = processDepartmentIds({
    cookieValue: cookieDepartmentIds,
    urlParams: getDepartmentIdsFromUrl(searchParams),
    queryValue: query?.department_ids,
    includeNullDept: true,
    nullDeptValue: nullDept ?? query?.null_department,
  });

  const teamIdsIntegers = processTeamIds({
    cookieValue: cookieTeamIds,
    urlParams: getTeamIdsFromUrl(searchParams),
  });

  return {
    payrollId,
    startDate,
    endDate,
    departmentIdsIntegers,
    teamIdsIntegers,
  };
};

/**
 * Deep comparison of two arrays (order-independent)
 * @param {Array} arr1 - First array to compare
 * @param {Array} arr2 - Second array to compare
 * @returns {boolean} True if arrays are deeply equal
 */
export const deepCompareArrays = (arr1, arr2) => {
  // Check if both arguments are arrays
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
    return false;
  }

  // Check if they have the same length
  if (arr1.length !== arr2.length) {
    return false;
  }

  // Sort both arrays to ensure order doesn't matter for comparison
  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();

  // Compare each element
  for (let i = 0; i < sorted1.length; i++) {
    const el1 = sorted1[i];
    const el2 = sorted2[i];

    const areBothObjects =
      el1 && typeof el1 === "object" && el2 && typeof el2 === "object";

    if (areBothObjects) {
      // If elements are objects or arrays, recursively compare
      if (!deepCompareArrays(el1, el2)) {
        return false;
      }
    } else if (el1 !== el2) {
      // For primitive values, check strict equality
      return false;
    }
  }
  return true;
};

export function secondsToHHMM(seconds) {
  if (!seconds) return "00:00";
  let hours = Math.floor(+seconds / 3600);
  let minutes = Math.floor((+seconds % 3600) / 60);
  if (hours < 10) hours = `0${hours}`;
  if (minutes < 10) minutes = `0${minutes}`;
  return `${hours}:${minutes}`;
}

export function floorSeconds(seconds) {
  const floorSecs = Math.floor(+seconds / 60) * 60;
  return floorSecs;
}

export function secondsToHours(seconds) {
  const hours = (seconds / 3600).toFixed(2);
  return hours;
}

export function toTitleCase(name) {
  if (!name || !name.length) return true;
  const newName = name.toLowerCase();
  return newName[0].toUpperCase() + newName.slice(1);
}

export function exportFile(data, filename) {
  if (window.navigator.msSaveOrOpenBlob) {
    const a = document.createElement("download");
    document.body.appendChild(a);
    a.style.display = "none";
    a.onclick = (evx) => {
      const myBlob = new Blob([data.action.payload], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      window.navigator.msSaveOrOpenBlob(myBlob, filename);
    };
    a.click();
  }
  // chrome ,safari and mozilla browsers
  else {
    const myBlob = new Blob([data.action.payload], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(myBlob);
    const element = document.createElement("a");
    document.body.appendChild(element);
    element.href = url;
    element.download = filename;
    element.click();
  }
}

export function exportTextFile(data, filename) {
  const textData = new TextDecoder().decode(data.action.payload);
  const correctedText = textData.replace(/\\n/g, "\n").replace(/"/g, "");

  const myBlob = new Blob([correctedText], { type: "text/plain" });

  if (window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(myBlob, filename);
  } else {
    const url = window.URL.createObjectURL(myBlob);
    const element = document.createElement("a");
    document.body.appendChild(element);
    element.href = url;
    element.download = filename;
    element.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(element);
  }
}

export const buildDepartmentAndTeamOptions = ({
  departmentAll = [],
  teamAll = [],
  userExtraData = {},
}) => {
  let departmentOptions = [];
  let teamOptions = [];

  // Departments
  if (departmentAll && departmentAll.length) {
    departmentOptions = departmentAll.map((item) => ({
      value: item.id,
      label: item.name,
    }));
  }

  if (!userExtraData?.department) {
    departmentOptions.unshift({
      value: "",
      label: "All Departments",
    });
  }

  // Teams
  if (teamAll && teamAll.length) {
    teamOptions = teamAll.map((item) => ({
      value: item.id,
      label: item.name,
    }));
  }

  if (!userExtraData?.team) {
    teamOptions.unshift({
      value: "",
      label: "All Teams",
    });
  }

  return {
    departmentOptions,
    teamOptions,
  };
};

export function convertToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () =>
      resolve(reader.result.replace("data:", "").replace(/^.+,/, ""));
    reader.onerror = (error) => reject(error);
  });
}

/**
 *
 * function is used to add all the adjustment data in their respective keys
 *
 * @export
 * @param {Array} list : list will be an array which will be
 * @param {string} key : will be the key used fetch all the data under it
 * @return {Array} : Array of object with sum of their keys
 */
export function tallyLists(list, key) {
  let result = [];
  // eslint-disable-next-line no-unused-expressions
  list?.forEach((item) => {
    // eslint-disable-next-line no-unused-expressions
    item?.[key]?.forEach((adjItem) => {
      let existing = result.find((r) => r.name === adjItem.name);
      if (existing) {
        existing.regular_time += adjItem.regular_time;
        existing.over_time += adjItem.over_time;
        existing.double_over_time += adjItem.double_over_time;
      } else {
        result.push({ ...adjItem });
      }
    });
  });
  return result?.length > 0 ? result : [];
}

export default {
  secondsToHHMM,
  secondsToHours,
  floorSeconds,
  toTitleCase,
  exportFile,
  exportTextFile,
  convertToBase64,
};
